<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click="close"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Attach Meter To Radio</h2>
        <div class="debug-info">
          <span>{{availableDevices.length}} items</span>
        </div>
        <button class="close-button" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>Loading available meters...</span>
        </div>
        <div v-else-if="error" class="error-state">
          <div class="error-message">{{ error }}</div>
          <button class="retry-button" @click="fetchAvailableDevices">Retry</button>
        </div>
        <div v-else class="form-content">
          <!-- Source device info -->
          <div class="source-info">
            <div class="info-label">Radio Device:</div>
            <div class="info-value">{{ deviceId }}</div>
          </div>

          <!-- Search filters -->
          <div class="search-filters">
            <div class="search-input-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                  type="text"
                  class="search-input"
                  v-model="searchQuery"
                  placeholder="Search by Logical Device Number..."
                  @input="handleSearchInput"
              >
            </div>
          </div>

          <!-- Device selection table -->
          <div class="device-table-container">
            <div v-if="availableDevices.length === 0" class="no-results">
              No available meters found matching your criteria.
            </div>
            <table v-else class="device-table">
              <thead>
              <tr>
                <th>Select</th>
                <th @click="sortBy('logical_device_number')" class="sortable-header">
                  LDN
                  <span v-if="sortColumn === 'logical_device_number'" class="sort-indicator">
                      {{ sortDirection === 'asc' ? '↑' : '↓' }}
                    </span>
                </th>
                <th @click="sortBy('type')" class="sortable-header">
                  Type
                  <span v-if="sortColumn === 'type'" class="sort-indicator">
                      {{ sortDirection === 'asc' ? '↑' : '↓' }}
                    </span>
                </th>
                <th>Customer Number</th>
                <th>Caliber</th>
                <th>Protocol</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="device in availableDevices" :key="device.logical_device_number" :class="{ 'selected-row': selectedDevice === device.logical_device_number }">
                <td>
                  <div class="radio-container">
                    <input
                        type="radio"
                        :id="'device-' + device.logical_device_number"
                        name="device-selection"
                        :value="device.logical_device_number"
                        v-model="selectedDevice"
                    >
                    <label :for="'device-' + device.logical_device_number" class="radio-label"></label>
                  </div>
                </td>
                <td>{{ device.logical_device_number }}</td>
                <td>{{ device.type || 'N/A' }}</td>
                <td>{{ device.customer_number || 'N/A' }}</td>
                <td>{{ device.caliber || 'N/A' }}</td>
                <td>{{ device.protocol || 'N/A' }}</td>
              </tr>
              </tbody>
            </table>

            <!-- Pagination controls -->
            <div v-if="availableDevices.length > 0" class="pagination-controls">
              <div class="pagination-info">
                Showing {{ startItem }}-{{ endItem }} of {{ meta.total }} items
              </div>
              <div class="pagination-buttons">
                <button
                    class="pagination-button"
                    :disabled="meta.page === 1"
                    @click="changePage(meta.page - 1)"
                >
                  Previous
                </button>
                <div class="page-numbers">
                  <button
                      v-for="page in visiblePageNumbers"
                      :key="page"
                      :class="['page-number', { active: meta.page === page }]"
                      @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                </div>
                <button
                    class="pagination-button"
                    :disabled="meta.page === meta.totalPages"
                    @click="changePage(meta.page + 1)"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div v-if="attachError" class="attach-error-message">{{ attachError }}</div>
        </div>
      </div>

      <div class="edit-actions">
        <button class="action-button" @click="close" :disabled="loading">Cancel</button>
        <button
            class="action-button retry-button"
            @click="fetchAvailableDevices"
            :disabled="loading"
        >
          Refresh List
        </button>
        <button
            class="action-button submit-button"
            @click="attachMeterToRadio"
            :disabled="loading || !selectedDevice"
        >
          Attach Meter
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceDetailsAddMeterToRadio',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      availableDevices: [],
      selectedDevice: '',
      searchQuery: '',
      loading: false,
      error: '',
      attachError: '',
      // Sorting
      sortColumn: 'logical_device_number',
      sortDirection: 'asc',
      // Pagination
      meta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
      },
      searchTimeout: null
    };
  },
  computed: {
    startItem() {
      return this.availableDevices.length === 0 ? 0 : ((this.meta.page - 1) * this.meta.limit) + 1;
    },
    endItem() {
      return Math.min(this.startItem + this.availableDevices.length - 1, this.meta.total);
    },
    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.meta.page - half);
      let end = Math.min(this.meta.totalPages, start + maxVisible - 1);

      if (end === this.meta.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetFilters();
        this.$nextTick(() => {
          this.fetchAvailableDevices();
        });
      }
    }
  },

  mounted() {
    if (this.isVisible) {
      this.resetFilters();
      this.$nextTick(() => {
        this.fetchAvailableDevices();
      });
    }
  },
  methods: {
    resetFilters() {
      this.searchQuery = '';
      this.meta.page = 1;
      this.selectedDevice = '';
    },
    sortBy(column, toggleDirection = true) {
      // If clicking the same column, toggle direction
      if (this.sortColumn === column && toggleDirection) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        if (toggleDirection) {
          this.sortDirection = 'asc';
        }
      }

      if (!this.availableDevices || this.availableDevices.length === 0) {
        return;
      }

      try {
        // Sort the available list
        this.availableDevices.sort((a, b) => {
          const valueA = (a[column] || '').toString().toLowerCase();
          const valueB = (b[column] || '').toString().toLowerCase();

          if (this.sortDirection === 'asc') {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
        });
      } catch (err) {
        // Silent error handling for sort operation
        console.error('Error during sorting:', err);
      }
    },

    handleSearchInput() {
      // Debounce search to avoid too many API calls
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.meta.page = 1; // Reset to first page on new search
        this.fetchAvailableDevices();
      }, 300);
    },

    async fetchAvailableDevices() {
      this.loading = true;
      this.error = '';
      this.attachError = '';

      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

      // Build query parameters
      const params = new URLSearchParams({
        page: this.meta.page,
        limit: this.meta.limit
      });

      // Add search filters if they exist
      if (this.searchQuery) {
        params.append('ldn', this.searchQuery.trim());
      }

      // Use the endpoint for MECHANICAL devices without radios
      const url = `${apiBaseUrl}/devices/no-radio?${params.toString()}`;

      try {
        // Add debug logging to see the URL
        console.log(`Fetching from URL: ${url}`);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching available meters: ${response.statusText}`);
        }

        const data = await response.json();

        // Log received data structure
        console.log('Response data structure:', JSON.stringify(data).substring(0, 100) + '...');

        // Handle response format with pagination
        if (data.devices && typeof data.total_devices !== 'undefined') {
          this.availableDevices = data.devices;
          this.meta = {
            page: data.page,
            limit: data.limit,
            total: data.total_devices,
            totalPages: data.total_pages
          };
          console.log(`Found ${this.availableDevices.length} meters out of ${data.total_devices} total`);
        } else if (Array.isArray(data)) {
          this.availableDevices = data;
          this.meta = {
            page: 1,
            limit: data.length,
            total: data.length,
            totalPages: 1
          };
          console.log(`Found ${this.availableDevices.length} meters (array response)`);
        } else {
          this.error = 'Unexpected API response format';
          this.availableDevices = [];
          console.error('Unexpected response format:', data);
        }

        if (this.availableDevices.length > 0) {
          this.availableDevices.sort((a, b) => {
            const valueA = (a[this.sortColumn] || '').toString().toLowerCase();
            const valueB = (b[this.sortColumn] || '').toString().toLowerCase();

            if (this.sortDirection === 'asc') {
              return valueA.localeCompare(valueB);
            } else {
              return valueB.localeCompare(valueA);
            }
          });
        }
      } catch (err) {
        console.error('Error fetching meters:', err);
        this.error = 'Failed to load available meters. Please try again.';
        this.availableDevices = [];
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.meta.totalPages) {
        this.meta.page = page;
        this.fetchAvailableDevices();
      }
    },

    async attachMeterToRadio() {
      if (!this.selectedDevice) {
        return;
      }

      this.loading = true;
      this.attachError = '';

      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

      // Use the new dedicated endpoint for installing a meter to a radio
      const url = `${apiBaseUrl}/devices/${this.deviceId}/install-meter`;

      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            // The meter is the selected device
            // The radio device number is the deviceId from the props (current device)
            meter_logical_number: this.selectedDevice,
            radio_device_number: this.deviceId
          })
        });

        if (response.ok) {
          // Emit the event with the selected device to notify parent component
          this.$emit('meter-attached', this.selectedDevice);
          this.close();
        } else {
          const resData = await response.json();

          const msg = resData.message ? resData.message.toLowerCase() : '';
          if (msg.includes('not found')) {
            this.attachError = 'Device not found';
          } else if (msg.includes('already installed') || msg.includes('already attached')) {
            this.attachError = 'This meter already has a radio attached or is already in use';
          } else {
            this.attachError = resData.message || 'An error occurred while attaching the meter';
          }
        }
      } catch (err) {
        console.error('Error attaching meter:', err);
        this.attachError = 'Failed to attach meter to radio. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.selectedDevice = '';
      this.searchQuery = '';
      this.meta.page = 1;
      this.error = '';
      this.attachError = '';
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32,32,32,0.1) 0%, rgba(16,16,16,0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 800px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50,50,50,0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 16px;
}

.debug-info {
  position: absolute;
  top: 10px;
  right: 50%;
  font-size: 11px;
  color: rgba(255,255,255,0.5);
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255,255,255,0.9);
}

/* Form container */
.form-container {
  position: relative;
  min-height: 300px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0,0,0,0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error states */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.attach-error-message {
  color: #ff8a80;
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255,255,255,0.1);
}

/* Source info */
.source-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: rgba(255,255,255,0.05);
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.1);
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  margin-right: 10px;
}

.info-value {
  font-size: 14px;
  font-weight: 700;
  color: rgba(79,195,247,0.9);
}

/* Search filters */
.search-filters {
  margin-bottom: 16px;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255,255,255,0.5);
}

.search-input {
  width: 100%;
  background-color: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: white;
  padding: 10px 12px 10px 36px;
  font-size: 13px;
  transition: border-color 0.2s, background-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: rgba(79,195,247,0.5);
  background-color: rgba(79,195,247,0.05);
}

/* Device Table */
.device-table-container {
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 16px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.1);
}

.device-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 12px;
}

.device-table th {
  background-color: rgba(0,0,0,0.2);
  padding: 12px 16px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: rgba(0,0,0,0.3);
}

.sort-indicator {
  margin-left: 4px;
  display: inline-block;
}

.device-table td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.device-table tr:hover {
  background-color: rgba(255,255,255,0.05);
}

.selected-row {
  background-color: rgba(79,195,247,0.1);
}

.selected-row:hover {
  background-color: rgba(79,195,247,0.15);
}

.no-results {
  padding: 30px 16px;
  text-align: center;
  color: rgba(255,255,255,0.6);
  font-size: 13px;
}

/* Radio button styling */
.radio-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-container input[type="radio"] {
  display: none;
}

.radio-label {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  position: relative;
  cursor: pointer;
}

.radio-label::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(79,195,247,0.8);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.2s ease-in-out;
}

input[type="radio"]:checked + .radio-label {
  border-color: rgba(79,195,247,0.8);
}

input[type="radio"]:checked + .radio-label::after {
  transform: translate(-50%, -50%) scale(1);
}

/* Pagination controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: rgba(0,0,0,0.1);
  border-top: 1px solid rgba(255,255,255,0.05);
}

.pagination-info {
  font-size: 11px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255,255,255,0.1);
}

.page-number.active {
  background-color: rgba(255,255,255,0.2);
  font-weight: 600;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79,195,247,0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79,195,247,0.3);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .edit-overlay-content {
    padding: 16px;
  }

  .device-table {
    font-size: 11px;
  }

  .device-table th,
  .device-table td {
    padding: 10px 8px;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-info {
    text-align: center;
  }

  .pagination-buttons {
    justify-content: center;
  }
}
</style>